// admin-panel/src/pages/QuoteForm.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { quotesApi } from '../lib/api';
import { ArrowLeft, Plus, Trash2, Save, Send } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_ITEM = { description: '', qty: 1, unit: 'sqft', price: 0, total: 0 };

export default function QuoteForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = !!id;

  const [form, setForm] = useState({
    client_name: '', client_phone: '', client_email: '', client_address: '',
    project_type: '', room_type: '', area_sqft: '', timeline: '',
    requirements: '', notes: '',
    terms: 'Payment: 50% advance, 50% on completion.\nValidity: 30 days from quote date.',
    discount_percent: 0, tax_percent: 18, validity_days: 30,
  });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);

  const { data: quote } = useQuery({ queryKey: ['quote', id], queryFn: () => quotesApi.get(id), enabled: isEdit });

  useEffect(() => {
    if (quote) {
      setForm({ client_name: quote.client_name||'', client_phone: quote.client_phone||'', client_email: quote.client_email||'', client_address: quote.client_address||'', project_type: quote.project_type||'', room_type: quote.room_type||'', area_sqft: quote.area_sqft||'', timeline: quote.timeline||'', requirements: quote.requirements||'', notes: quote.notes||'', terms: quote.terms||form.terms, discount_percent: quote.discount_percent||0, tax_percent: quote.tax_percent||18, validity_days: quote.validity_days||30 });
      if (quote.items?.length) setItems(quote.items.map(i => ({ ...i, total: i.qty * i.price })));
    }
  }, [quote]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Line item math
  const addItem = () => setItems(prev => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => {
    setItems(prev => prev.map((it, idx) => {
      if (idx !== i) return it;
      const updated = { ...it, [k]: v };
      if (k === 'qty' || k === 'price') updated.total = Number(updated.qty) * Number(updated.price);
      return updated;
    }));
  };

  const subtotal = items.reduce((s, i) => s + Number(i.total), 0);
  const discountAmt = subtotal * (Number(form.discount_percent) / 100);
  const afterDiscount = subtotal - discountAmt;
  const taxAmt = afterDiscount * (Number(form.tax_percent) / 100);
  const total = afterDiscount + taxAmt;

  const saveMut = useMutation({
    mutationFn: (payload) => isEdit ? quotesApi.update(id, payload) : quotesApi.create(payload),
    onSuccess: (res) => {
      qc.invalidateQueries(['admin-quotes']);
      toast.success(isEdit ? 'Quote updated!' : 'Quote created!');
      if (!isEdit) navigate(`/quotes/${res.quote?.id}/edit`);
    },
    onError: (e) => toast.error(e.message || 'Save failed'),
  });

  function handleSave(sendAfter = false) {
    if (!form.client_name || !form.client_phone) return toast.error('Client name and phone are required');
    saveMut.mutate({
      ...form,
      items,
      subtotal,
      discount_amount: discountAmt,
      tax_amount: taxAmt,
      total_amount: total,
    });
  }

  const INPUT = "w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30";

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/quotes')} className="p-2 rounded-xl bg-[#151515] border border-white/5 text-[#888] hover:text-white transition-all"><ArrowLeft size={16} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{isEdit ? `Edit Quote · ${quote?.quote_number}` : 'New Quote'}</h1>
        </div>
        <button onClick={() => handleSave()} disabled={saveMut.isPending}
          className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
          <Save size={15} /> {saveMut.isPending ? 'Saving…' : 'Save Quote'}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: Client + Items */}
        <div className="lg:col-span-2 space-y-4">
          {/* Client Info */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Client Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[{k:'client_name',l:'Client Name *',p:'Full name'},{k:'client_phone',l:'Phone *',p:'+91 98765 43210'},{k:'client_email',l:'Email',p:'client@email.com'},{k:'client_address',l:'Project Location',p:'Area, City'}].map(({k,l,p})=>(
                <div key={k}>
                  <label className="block text-xs text-[#555] mb-1.5">{l}</label>
                  <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={p} className={INPUT} />
                </div>
              ))}
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Project Type</label>
                <input value={form.project_type} onChange={e=>set('project_type',e.target.value)} placeholder="e.g. Modular Kitchen" className={INPUT} />
              </div>
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Timeline</label>
                <input value={form.timeline} onChange={e=>set('timeline',e.target.value)} placeholder="e.g. 6-8 weeks" className={INPUT} />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Quote Items</h3>
            <div className="space-y-2 mb-4">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 text-xs text-[#555] px-1">
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-2">Unit</div>
                <div className="col-span-2">Rate (₹)</div>
                <div className="col-span-1"></div>
              </div>
              {items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <input value={item.description} onChange={e=>updateItem(i,'description',e.target.value)} placeholder="Item description…"
                      className="w-full bg-[#1A1A1A] border border-white/5 rounded-lg px-3 py-2 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" min="0" value={item.qty} onChange={e=>updateItem(i,'qty',e.target.value)}
                      className="w-full bg-[#1A1A1A] border border-white/5 rounded-lg px-2 py-2 text-sm text-white text-center focus:outline-none focus:border-[#D4AF37]/30" />
                  </div>
                  <div className="col-span-2">
                    <select value={item.unit} onChange={e=>updateItem(i,'unit',e.target.value)}
                      className="w-full bg-[#1A1A1A] border border-white/5 rounded-lg px-2 py-2 text-sm text-white focus:outline-none">
                      {['sqft','unit','rft','set','lot'].map(u=><option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input type="number" min="0" value={item.price} onChange={e=>updateItem(i,'price',e.target.value)}
                      className="w-full bg-[#1A1A1A] border border-white/5 rounded-lg px-2 py-2 text-sm text-white text-right focus:outline-none focus:border-[#D4AF37]/30" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button onClick={()=>removeItem(i)} className="p-1.5 text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13}/></button>
                  </div>
                  <div className="col-span-11 text-right text-xs text-[#555] -mt-1">= ₹{Number(item.total).toLocaleString('en-IN')}</div>
                </div>
              ))}
            </div>
            <button onClick={addItem} className="flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#E8C84A] transition-colors">
              <Plus size={15} /> Add Line Item
            </button>
          </div>

          {/* Notes / Terms */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white">Notes & Terms</h3>
            {[{k:'requirements',l:'Requirements / Scope',p:'Project scope and special requirements…',rows:3},{k:'notes',l:'Internal Notes',p:'Notes visible only to admin…',rows:2},{k:'terms',l:'Terms & Conditions',p:'Payment terms, warranty…',rows:3}].map(({k,l,p,rows})=>(
              <div key={k}>
                <label className="block text-xs text-[#555] mb-1.5">{l}</label>
                <textarea value={form[k]} onChange={e=>set(k,e.target.value)} rows={rows} placeholder={p} className={`${INPUT} resize-none`} />
              </div>
            ))}
          </div>
        </div>

        {/* Right: Summary */}
        <div className="space-y-4">
          <div className="bg-[#151515] border border-white/5 rounded-xl p-5 sticky top-24">
            <h3 className="text-sm font-semibold text-white mb-4">Quote Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#666]">Subtotal</span>
                <span className="text-white">₹{subtotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#666] w-24">Discount %</span>
                <input type="number" min="0" max="100" value={form.discount_percent} onChange={e=>set('discount_percent',e.target.value)}
                  className="w-16 bg-[#1A1A1A] border border-white/5 rounded-lg px-2 py-1.5 text-sm text-white text-center focus:outline-none focus:border-[#D4AF37]/30" />
                <span className="text-sm text-red-400 ml-auto">-₹{discountAmt.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#666] w-24">Tax (GST) %</span>
                <input type="number" min="0" max="100" value={form.tax_percent} onChange={e=>set('tax_percent',e.target.value)}
                  className="w-16 bg-[#1A1A1A] border border-white/5 rounded-lg px-2 py-1.5 text-sm text-white text-center focus:outline-none focus:border-[#D4AF37]/30" />
                <span className="text-sm text-[#888] ml-auto">+₹{taxAmt.toLocaleString('en-IN')}</span>
              </div>
              <div className="h-px bg-white/5" />
              <div className="flex justify-between">
                <span className="text-white font-semibold">Total</span>
                <span className="text-[#D4AF37] font-bold text-xl">₹{total.toLocaleString('en-IN')}</span>
              </div>
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Validity (days)</label>
                <input type="number" min="1" value={form.validity_days} onChange={e=>set('validity_days',e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30" />
              </div>
            </div>
            <div className="mt-5 space-y-2">
              <button onClick={() => handleSave()} disabled={saveMut.isPending}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
                <Save size={15}/> {saveMut.isPending ? 'Saving…' : 'Save Quote'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
